import React, { useEffect, useMemo, useRef, useState } from "react";
import { ScheduleTemplateStatus } from "@common/types/domains/accounting/schedule";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { BaseCellProps } from "@components/spreadsheet/cells";
import { Picker } from "@design-system/Picker/Picker.lib";
import { BreakableText } from "@design-system/BreakableText/BreakableText";

type Props = BaseCellProps<string>;

export const TransactionSplitScheduleCell: React.FC<Props> = ({
    value,
    isEditing,
    finishEditing,
    enterEditModeKey,
}) => {
    const templatesQuery = useScheduleTemplates();
    const [isOpen, setIsOpen] = useState(false);
    const selectionMadeRef = useRef(false);
    const [search, setSearch] = useState("");
    const trimmedSearch = search.trim().toLowerCase();

    const templates = useMemo(() => {
        const allTemplates = templatesQuery.data ?? [];
        const activeTemplates = allTemplates.filter(
            (template) => template.status === ScheduleTemplateStatus.active,
        );
        const selectedTemplate = allTemplates.find(
            (template) => template.id === value,
        );
        if (
            selectedTemplate != null &&
            !activeTemplates.some(
                (template) => template.id === selectedTemplate.id,
            )
        ) {
            return [...activeTemplates, selectedTemplate];
        }
        return activeTemplates;
    }, [templatesQuery.data, value]);

    const filteredTemplates = useMemo(() => {
        if (trimmedSearch === "") {
            return templates;
        }
        return templates.filter((template) =>
            template.name.toLowerCase().includes(trimmedSearch),
        );
    }, [templates, trimmedSearch]);

    const selectedTemplate = templates.find(
        (template) => template.id === value,
    );

    useEffect(() => {
        if (isEditing) {
            setIsOpen(true);
        }
    }, [isEditing]);

    if (isEditing) {
        return (
            <div
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <Picker.Select
                    searchable
                    cellEditor={{ openingKey: enterEditModeKey }}
                    value={selectedTemplate?.id ?? null}
                    open={isOpen}
                    onValueChange={(templateId) => {
                        selectionMadeRef.current = true;
                        finishEditing(templateId);
                        setSearch("");
                        setIsOpen(false);
                    }}
                    onInputValueChange={setSearch}
                    onOpenChange={(open) => {
                        setIsOpen(open);
                        if (!open && !selectionMadeRef.current) {
                            finishEditing();
                        }
                        selectionMadeRef.current = false;
                    }}
                >
                    <Picker.ButtonTrigger
                        render={<Picker.Button variant="primary" size="sm" />}
                        value={selectedTemplate?.name}
                        placeholder="Select schedule"
                    />
                    <Picker.Popup sideOffset={4}>
                        <Picker.Search placeholder="Search policies..." />
                        <Picker.List>
                            {filteredTemplates.length === 0 && (
                                <Picker.Empty>
                                    No matching schedules
                                </Picker.Empty>
                            )}
                            {filteredTemplates.map((template) => (
                                <Picker.Item
                                    key={template.id}
                                    value={template.id}
                                >
                                    {template.name}
                                </Picker.Item>
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </div>
        );
    }

    if (!selectedTemplate) {
        return null;
    }

    return <BreakableText text={selectedTemplate.name} showTooltip />;
};
